import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Phone, MapPin, Clock, Star, ChevronDown, ChevronLeft, ChevronRight, Menu, X, 
  Mail, Users, Calendar, Instagram, Facebook,
  Utensils, Wine, Flame, Gift, Music, Car, Dog, 
  Wifi, CreditCard, ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Calendar as CalendarUI } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_cocktail-brough/artifacts/zjbrnf42_2.jpeg",
  exterior: "https://customer-assets.emergentagent.com/job_cocktail-brough/artifacts/e4qd7ty9_1.jpeg",
  logo: "https://customer-assets.emergentagent.com/job_cocktail-brough/artifacts/j1tqxfg7_logo%20new.jpg",
  steak: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/o1v9ntu6_12.jpeg",
  cocktails: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/9cozctum_WhatsApp%20Image%202026-03-20%20at%2010.20.30.jpeg",
  burger: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/tggp6gps_6.jpeg",
};

const whatsOnImages = [
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/hkyi8sx9_m1.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/dawj5vie_m2.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/tbaylmhv_slwo.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/1ttov9yp_m4.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/6fj5mzqr_m5.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/rblas8br_m6.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/uhcttp5d_m8.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/d9k2v85q_m9.jpeg",
  "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/wpuqynh8_m10.jpeg",
];

const galleryPhotos = [
  "https://customer-assets.emergentagent.com/job_cocktail-brough/artifacts/zpzcu1e6_whatson7.jpg",
  "https://customer-assets.emergentagent.com/job_cocktail-brough/artifacts/hjl93ztj_whatson8.jpg",
  "https://customer-assets.emergentagent.com/job_cocktail-brough/artifacts/858prgl7_whatson2.jpg",
  "https://customer-assets.emergentagent.com/job_cocktail-brough/artifacts/40ssw7ab_whatson3.jpg",
];

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const reviews = [
  { name: "Louise M.", text: "Welcoming, friendly staff, amazing food and drinks and a wonderful atmosphere.", rating: 5 },
  { name: "Leanne B.", text: "The fillet and ribeye were unbelievably soft, cooked to perfection and SO tasty.", rating: 5 },
  { name: "Eve C.", text: "Love the new changes and what the new owner has done to the place!", rating: 5 },
  { name: "James P.", text: "Best cocktails in Brough, hands down. The atmosphere is incredible.", rating: 5 },
];

const facilities = [
  { icon: Car, label: "Parking" },
  { icon: Dog, label: "Dog Friendly" },
  { icon: Wifi, label: "Free WiFi" },
  { icon: CreditCard, label: "Cards Accepted" },
  { icon: Wine, label: "Cocktails" },
  { icon: Music, label: "Live Events" },
];

const foodSlideshow = [
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/bh6avfah_11.jpeg", label: "Steak & Chips" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/nf32wwrs_12.jpeg", label: "Steak Platter" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/h3axgpnl_14.jpeg", label: "Cheese & Charcuterie" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/e3y1g6rd_15.jpeg", label: "Sunday Roast" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/imehgzf0_5.jpeg", label: "Penne Arrabbiata" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/c7j980f6_6.jpeg", label: "Pizza" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/0ufpoysq_7.jpeg", label: "Chicken Supreme" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/0wgwnbwr_9.jpeg", label: "Coffee & Cake" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/cvbs34ip_10.jpeg", label: "Calzone" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/w3hbx6pw_nf1.jpeg", label: "Lasagne" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/1jra8dmb_nf2.jpeg", label: "Pie & Chips" },
  { image: "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/t3yvl3ke_nf4.jpeg", label: "Fish & Chips" },
];

const menuItems = [
  { title: "Steaks & Grill", desc: "Premium cuts cooked to perfection", icon: Flame, image: IMAGES.steak },
  { title: "Casual Dining", desc: "Pub classics & comfort food", icon: Utensils, image: IMAGES.burger },
  { title: "Cocktails", desc: "Expertly crafted drinks", icon: Wine, image: IMAGES.cocktails },
];

const timeSlots = [
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "17:00", "17:30", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00"
];

const RESOS_BOOKING_URL = "https://livs-lounge.resos.com/booking";

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [whatsOnPage, setWhatsOnPage] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const totalPages = Math.ceil(whatsOnImages.length / 3);
  const nextWhatsOn = () => setWhatsOnPage((prev) => (prev + 1) % totalPages);
  const prevWhatsOn = () => setWhatsOnPage((prev) => (prev - 1 + totalPages) % totalPages);

  const openBooking = () => window.open(RESOS_BOOKING_URL, '_blank');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail });
      toast.success("Subscribed!");
      setNewsletterEmail("");
    } catch (e) {
      toast.error(e.response?.status === 400 ? "Already subscribed!" : "Failed to subscribe.");
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f3f33]" data-testid="navigation">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4 md:gap-8">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white/80 hover:text-white">
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
              <div className="hidden lg:flex items-center gap-8">
                {["MENUS", "OFFERS", "WHAT'S ON"].map((item) => (
                  <button 
                    key={item} 
                    onClick={() => scrollTo(item === "MENUS" ? "menu" : item === "OFFERS" ? "offers" : "events")}
                    className="text-white/80 hover:text-gold text-xs font-medium tracking-widest transition-colors"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
            <a href="/" className="absolute left-1/2 -translate-x-1/2">
              <span className="font-serif text-gold text-lg md:text-xl lg:text-2xl tracking-wide">Liv's Lounge</span>
            </a>
            <Button 
              onClick={openBooking}
              className="bg-gold hover:bg-gold/90 text-[#0f3f33] font-medium text-[10px] md:text-xs tracking-widest px-3 md:px-5 h-8 md:h-9 rounded-sm"
              data-testid="nav-book-btn"
            >
              BOOK NOW
            </Button>
          </div>
        </div>
      </nav>

      {/* Info Bar */}
      <div className="fixed top-16 left-0 right-0 z-40 bg-[#0a0a0a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 md:py-2.5 flex justify-center items-center gap-4 md:gap-8 text-[10px] md:text-xs">
          <a href="https://maps.google.com/?q=1+Skillings+Ln,+Brough+HU15+1BA" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 md:gap-2 text-white/50 hover:text-gold transition-colors">
            <MapPin size={12} className="text-gold/70" />
            <span className="hidden sm:inline tracking-wide">1 SKILLINGS LN, BROUGH</span>
            <span className="sm:hidden tracking-wide">BROUGH</span>
          </a>
          <a href="tel:01482426705" className="flex items-center gap-1.5 md:gap-2 text-white/50 hover:text-gold transition-colors">
            <Phone size={12} className="text-gold/70" />
            <span className="tracking-wide">01482 426705</span>
          </a>
          <div className="hidden md:flex items-center gap-2 text-white/50">
            <Clock size={12} className="text-gold/70" />
            <span className="tracking-wide">OPEN DAILY 10AM</span>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          <motion.div 
            initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
            className="fixed top-16 left-0 w-72 h-[calc(100vh-64px)] bg-[#0f3f33] z-50 overflow-y-auto"
          >
            <div className="p-8 space-y-1">
              {[
                { label: "Home", id: "hero" },
                { label: "About", id: "about" },
                { label: "Menus", id: "menu" },
                { label: "What's On", id: "events" },
                { label: "Offers", id: "offers" },
                { label: "Contact", id: "contact" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => { scrollTo(item.id); setMobileMenuOpen(false); }}
                  className="block w-full text-left text-white/80 hover:text-gold py-3 text-sm font-medium tracking-wide border-b border-white/5 transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-8 flex gap-4">
                <a href="https://www.instagram.com/livsloungebrough/" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-gold">
                  <Instagram size={20} />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61566999039890" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-gold">
                  <Facebook size={20} />
                </a>
              </div>
            </div>
          </motion.div>
          <div className="fixed inset-0 bg-black/60 z-40 top-16" onClick={() => setMobileMenuOpen(false)} />
        </>
      )}

      {/* Hero */}
      <section id="hero" className="relative h-screen flex items-center pt-28">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Liv's Lounge" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0a0a0a]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-gold text-sm tracking-[0.3em] mb-6 drop-shadow-lg">RESTAURANT & COCKTAIL LOUNGE</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-6 leading-[1.1] drop-shadow-lg">
              Welcome to<br/><span className="text-gold drop-shadow-lg">Liv's Lounge</span>
            </h1>
            <p className="text-white/90 text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed drop-shadow-md">
              Great food, handcrafted cocktails and a relaxed atmosphere in the heart of Brough
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openBooking}
                className="bg-gold hover:bg-gold/90 text-[#0f3f33] font-medium text-sm tracking-wider px-10 h-12 rounded-sm"
                data-testid="hero-book-btn"
              >
                BOOK A TABLE
              </Button>
              <Button 
                onClick={() => scrollTo("menu")}
                className="bg-[#0f3f33] hover:bg-[#0a2d24] text-white border border-[#0f3f33] text-sm tracking-wider px-10 h-12 rounded-sm"
              >
                VIEW MENU
              </Button>
            </div>
          </motion.div>
        </div>
        <button onClick={() => scrollTo("about")} className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/50 hover:text-gold transition-colors">
          <ChevronDown size={28} />
        </button>
      </section>

      {/* About - Grand Welcome */}
      <section id="about" className="py-32 px-6 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          {/* Grand Header */}
          <motion.div {...fadeIn} className="text-center mb-20">
            <p className="text-gold text-sm tracking-[0.4em] mb-6">WELCOME TO</p>
            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-8">
              Liv's <span className="italic text-gold">Lounge</span>
            </h2>
            <div className="w-24 h-[1px] bg-gold mx-auto mb-8" />
            <p className="text-white/60 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              A vibrant restaurant and cocktail lounge in the heart of Brough, offering 
              exceptional food, expertly crafted drinks, and an unforgettable atmosphere.
            </p>
          </motion.div>

          {/* Image & Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div {...fadeIn} className="relative">
              <div className="relative">
                <img src={IMAGES.exterior} alt="Liv's Lounge" className="w-full rounded-sm" />
              </div>
            </motion.div>
            
            <motion.div {...fadeIn} className="lg:pl-8">
              <h3 className="font-serif text-3xl md:text-4xl text-white mb-6">
                Where Every Visit<br/>Becomes a <span className="text-gold">Memory</span>
              </h3>
              <p className="text-white/50 leading-relaxed mb-8">
                Whether you're joining us for an intimate dinner, celebrating a special occasion, 
                or enjoying cocktails with friends, our welcoming team and beautifully prepared 
                dishes make every moment special. Experience the perfect blend of great food, 
                stunning drinks, and warm hospitality.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-6 md:gap-8 mb-8">
                <div className="text-center">
                  <p className="text-gold text-2xl md:text-3xl font-serif">£10-20</p>
                  <p className="text-white/40 text-[10px] md:text-xs tracking-wider">PER PERSON</p>
                </div>
                <div className="w-[1px] bg-white/10 hidden md:block" />
                <div className="text-center">
                  <p className="text-gold text-2xl md:text-3xl font-serif">10am</p>
                  <p className="text-white/40 text-[10px] md:text-xs tracking-wider">OPEN DAILY</p>
                </div>
                <div className="w-[1px] bg-white/10 hidden md:block" />
                <div className="text-center">
                  <p className="text-gold text-2xl md:text-3xl font-serif">5★</p>
                  <p className="text-white/40 text-[10px] md:text-xs tracking-wider">RATED</p>
                </div>
              </div>
              <Button 
                onClick={openBooking}
                className="bg-[#0f3f33] hover:bg-[#0a2d24] text-white font-medium text-sm tracking-wider px-8 h-12 rounded-sm border border-[#0f3f33] hover:border-gold"
              >
                RESERVE YOUR TABLE
              </Button>
            </motion.div>
          </div>

          {/* Facilities */}
          <motion.div {...fadeIn} className="border-t border-white/5 pt-16">
            <p className="text-center text-white/30 text-xs tracking-[0.3em] mb-10">AMENITIES</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {facilities.map((f, i) => (
                <div key={i} className="group bg-gradient-to-b from-white/[0.03] to-transparent border border-white/5 hover:border-gold/30 rounded-sm p-5 md:p-6 transition-all duration-300">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[#0f3f33]/50 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                      <f.icon className="text-gold" size={18} />
                    </div>
                    <p className="text-white/70 text-sm font-medium group-hover:text-white transition-colors">{f.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="py-24 bg-[#0d0d0d]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-12">
            <p className="text-gold text-xs tracking-[0.3em] mb-4">OUR MENU</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">Food Worth Coming Back For</h2>
          </motion.div>
        </div>

        {/* Auto-scrolling food slideshow */}
        <motion.div {...fadeIn} className="mb-16 overflow-hidden" data-testid="food-slideshow">
          <div className="food-slideshow-track">
            {[...foodSlideshow, ...foodSlideshow].map((item, i) => (
              <div key={i} className="flex-shrink-0 w-72 md:w-80 lg:w-96 mx-2 md:mx-3">
                <div className="overflow-hidden rounded-sm aspect-square">
                  <img 
                    src={item.image} 
                    alt="Food" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {menuItems.map((item, i) => (
              <motion.div 
                key={i} {...fadeIn}
                className="group relative overflow-hidden rounded-sm aspect-[4/5] border-2 border-transparent hover:border-[#0f3f33] transition-all duration-300"
              >
                <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-[#0f3f33]/90 transition-all duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <item.icon className="text-gold mb-3" size={24} />
                  <h3 className="text-white text-lg font-medium mb-1">{item.title}</h3>
                  <p className="text-white/60 text-sm">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Full Menu Images */}
          <motion.div {...fadeIn} className="mt-16">
            <p className="text-gold text-xs tracking-[0.3em] mb-8 text-center">VIEW OUR FULL MENUS</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/k0kb8hxs_nmenu1.jpeg",
                "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/ua2jyisx_nm2.jpeg",
                "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/qdtj3dnu_nm3.jpeg",
                "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/ocjetbq4_nm4.jpeg",
              ].map((src, i) => (
                <div 
                  key={i} 
                  className="cursor-pointer overflow-hidden rounded-sm border border-white/10 hover:border-gold/40 transition-all duration-300"
                  onClick={() => setLightboxImage(src)}
                  data-testid={`menu-image-${i}`}
                >
                  <img src={src} alt="Menu" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {[
                "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/hyyg45xa_newmenu2.jpeg",
                "https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/brshveau_newwebsite3.jpeg",
              ].map((src, i) => (
                <div 
                  key={i} 
                  className="cursor-pointer overflow-hidden rounded-sm border border-white/10 hover:border-gold/40 transition-all duration-300"
                  onClick={() => setLightboxImage(src)}
                  data-testid={`menu-image-extra-${i}`}
                >
                  <img src={src} alt="Menu" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
                </div>
              ))}
            </div>
            <div className="mt-4 max-w-2xl mx-auto">
              <div 
                className="cursor-pointer overflow-hidden rounded-sm border border-white/10 hover:border-gold/40 transition-all duration-300"
                onClick={() => setLightboxImage("https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/h9f0c8fp_new%20menu1.jpeg")}
                data-testid="menu-image-overview"
              >
                <img src="https://customer-assets.emergentagent.com/job_1b9da127-dc8a-4427-8889-75edc796fb14/artifacts/h9f0c8fp_new%20menu1.jpeg" alt="Liv's Lounge Overview" className="w-full h-auto object-contain hover:scale-[1.02] transition-transform duration-500" />
              </div>
            </div>
            <p className="text-white/40 text-xs text-center mt-4">Tap to enlarge</p>
          </motion.div>

          <motion.div {...fadeIn} className="mt-12 text-center">
            <blockquote className="text-white/70 text-lg italic font-serif max-w-2xl mx-auto">
              "The fillet and ribeye were unbelievably soft, cooked to perfection and SO tasty."
            </blockquote>
            <p className="text-gold text-sm mt-4">— Leanne B.</p>
          </motion.div>
        </div>
      </section>

      {/* What's On */}
      <section id="events" className="py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeIn} className="text-center mb-16">
            <p className="text-gold text-xs tracking-[0.3em] mb-4">WHAT'S ON</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white">What's On</h2>
          </motion.div>
          <div className="relative mb-12" data-testid="whats-on-carousel">
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${whatsOnPage * 100}%)` }}
              >
                {Array.from({ length: totalPages }).map((_, pageIdx) => (
                  <div key={pageIdx} className="w-full flex-shrink-0 grid grid-cols-3 gap-4">
                    {whatsOnImages.slice(pageIdx * 3, pageIdx * 3 + 3).map((src, i) => (
                      <div key={i} className="overflow-hidden rounded-sm bg-[#0f3f33] h-full cursor-pointer" onClick={() => setLightboxImage(src)}>
                        <img src={src} alt="What's On" className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <button 
              onClick={prevWhatsOn}
              className="absolute -left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-gold hover:text-[#0f3f33] transition-colors"
              data-testid="whats-on-prev"
            >
              <ChevronLeft size={20} />
            </button>
            <button 
              onClick={nextWhatsOn}
              className="absolute -right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-gold hover:text-[#0f3f33] transition-colors"
              data-testid="whats-on-next"
            >
              <ChevronRight size={20} />
            </button>
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button 
                  key={i} 
                  onClick={() => setWhatsOnPage(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === whatsOnPage ? 'bg-gold w-6' : 'bg-white/30 hover:bg-white/50'}`}
                />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {galleryPhotos.map((src, i) => (
              <motion.div key={i} {...fadeIn} className="aspect-square overflow-hidden rounded-sm border-2 border-transparent hover:border-[#0f3f33] transition-all duration-300">
                <img src={src} alt="Event" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 px-6 bg-[#0f3f33]">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeIn} className="text-center mb-16">
            <p className="text-gold text-xs tracking-[0.3em] mb-4">REVIEWS</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Loved by Guests</h2>
            <div className="flex items-center justify-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} size={18} className="fill-gold text-gold" />)}
              <span className="text-white/80 text-sm ml-2">5.0 on Google</span>
            </div>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {reviews.map((r, i) => (
              <motion.div key={i} {...fadeIn} className="bg-white/5 backdrop-blur-sm rounded-sm p-5 md:p-6 border border-transparent hover:border-gold/30 transition-all duration-300">
                <div className="flex gap-0.5 mb-3 md:mb-4">
                  {[...Array(r.rating)].map((_, j) => <Star key={j} size={12} className="fill-gold text-gold" />)}
                </div>
                <p className="text-white/80 text-sm leading-relaxed mb-3 md:mb-4">"{r.text}"</p>
                <p className="text-gold text-xs tracking-wider">— {r.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Section */}
      <section className="py-16 px-6 bg-gold">
        <div className="max-w-xl mx-auto text-center">
          <h3 className="font-serif text-2xl text-[#0f3f33] mb-2">Stay in the Loop</h3>
          <p className="text-[#0f3f33]/70 text-sm mb-6">Join our socials</p>
          <div className="flex justify-center gap-4">
            <a href="https://www.facebook.com/profile.php?id=61566999039890" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#0f3f33] flex items-center justify-center text-gold hover:bg-[#0a2d24] transition-colors">
              <Facebook size={22} />
            </a>
            <a href="https://www.instagram.com/livsloungebrough/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full bg-[#0f3f33] flex items-center justify-center text-gold hover:bg-[#0a2d24] transition-colors">
              <Instagram size={22} />
            </a>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div {...fadeIn}>
              <p className="text-gold text-xs tracking-[0.3em] mb-4">VISIT US</p>
              <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Find Liv's Lounge</h2>
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <MapPin className="text-gold mt-0.5" size={18} />
                  <div>
                    <p className="text-white text-sm">1 Skillings Ln</p>
                    <p className="text-white/50 text-sm">Brough HU15 1BA</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Clock className="text-gold mt-0.5 flex-shrink-0" size={18} />
                  <div className="w-full">
                    <p className="text-white text-sm font-medium mb-3">Opening Hours</p>
                    <div className="space-y-1.5 text-sm">
                      {[
                        { day: "Monday", hours: "10am – 10pm", food: "10am – 2pm" },
                        { day: "Tuesday", hours: "10am – 10pm", food: "10am – 2pm" },
                        { day: "Wednesday", hours: "10am – 11:30pm", food: "10am – 8pm" },
                        { day: "Thursday", hours: "10am – 11:30pm", food: "10am – 8pm" },
                        { day: "Friday", hours: "10am – 12am", food: "10am – 8pm" },
                        { day: "Saturday", hours: "10am – 12am", food: "10am – 8pm" },
                        { day: "Sunday", hours: "10am – 10pm", food: "10am – 5pm" },
                      ].map((row) => (
                        <div key={row.day} className="flex justify-between">
                          <span className="text-white/70 w-24">{row.day}</span>
                          <span className="text-white/50">{row.hours}</span>
                          <span className="text-gold/70 text-xs">Food {row.food}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-gold/80 text-xs mt-3 italic">Pizzas available to eat in or takeaway Fri/Sat until late</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="text-gold mt-0.5" size={18} />
                  <a href="tel:01482426705" className="text-gold text-sm hover:underline">01482 426705</a>
                </div>
              </div>
              <div className="flex gap-3">
                <a href="https://www.instagram.com/livsloungebrough/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center text-white/60 hover:bg-gold hover:text-[#0f3f33] transition-colors">
                  <Instagram size={18} />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61566999039890" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-sm bg-white/5 flex items-center justify-center text-white/60 hover:bg-gold hover:text-[#0f3f33] transition-colors">
                  <Facebook size={18} />
                </a>
              </div>
            </motion.div>
            <motion.div {...fadeIn}>
              <div className="bg-[#0d0d0d] rounded-sm p-8 border border-white/5 text-center flex flex-col items-center justify-center h-full">
                <Calendar className="text-gold mb-4" size={40} />
                <h3 className="font-serif text-2xl text-white mb-3">Book a Table</h3>
                <p className="text-white/50 text-sm mb-8 max-w-xs">Reserve your table online in just a few clicks using our booking system.</p>
                <Button 
                  onClick={openBooking}
                  className="bg-gold hover:bg-gold/90 text-[#0f3f33] font-medium text-sm tracking-wider px-10 h-12 rounded-sm w-full max-w-xs"
                  data-testid="contact-book-btn"
                >
                  BOOK A TABLE
                </Button>
                <p className="text-white/30 text-xs mt-4">Or call us on <a href="tel:01482426705" className="text-gold hover:underline">01482 426705</a></p>
                <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                  <div className="bg-[#0f3f33]/30 border border-[#0f3f33]/50 rounded-sm p-4">
                    <p className="text-gold text-sm font-medium">For bookings over 6, please call.</p>
                  </div>
                  <div className="bg-[#0f3f33]/30 border border-[#0f3f33]/50 rounded-sm p-4">
                    <p className="text-gold text-sm font-medium">The lounge is available for private hire/parties — please call for more info.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Street View & Map Side by Side */}
          <motion.div {...fadeIn} className="mt-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gold text-xs tracking-[0.3em] mb-3">STREET VIEW</p>
                <div className="w-full h-48 md:h-56 rounded-sm overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!4v1709654400000!6m8!1m7!1sO2hHqQ2-9e3fXkCLWIWueg!2m2!1d53.7294665!2d-0.5732978!3f107.08!4f-2.77!5f0.7820865974627469"
                    width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Street View"
                  />
                </div>
              </div>
              <div>
                <p className="text-gold text-xs tracking-[0.3em] mb-3">LOCATION</p>
                <div className="w-full h-48 md:h-56 rounded-sm overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2361.8068088093824!2d-0.5822!3d53.7273!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4878be7c4c2f7c31%3A0x4d9c5c8c8c8c8c8c!2s1%20Skillings%20Ln%2C%20Brough%20HU15%201BA%2C%20UK!5e0!3m2!1sen!2sus!4v1234567890"
                    width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="Location"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <span className="font-serif text-gold text-lg">Liv's Lounge</span>
            <div className="flex gap-6 text-white/40 text-xs tracking-wider">
              <span>1 Skillings Ln, Brough</span>
              <span>01482 426705</span>
            </div>
            <div className="flex gap-4">
              <a href="https://www.instagram.com/livsloungebrough/" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-gold transition-colors"><Instagram size={18} /></a>
              <a href="https://www.facebook.com/profile.php?id=61566999039890" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-gold transition-colors"><Facebook size={18} /></a>
            </div>
          </div>
          <p className="text-center text-white/20 text-xs mt-8">© {new Date().getFullYear()} Liv's Lounge. All rights reserved.</p>
        </div>
      </footer>

      {/* Mobile Book Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#0a0a0a]/95 backdrop-blur border-t border-white/5 md:hidden z-40">
        <Button onClick={openBooking} className="w-full bg-gold hover:bg-gold/90 text-[#0f3f33] font-medium h-12 rounded-sm">
          BOOK A TABLE
        </Button>
      </div>

      {/* Booking Dialog */}
      {/* Lightbox */}
      {lightboxImage && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 cursor-pointer backdrop-blur-sm"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox-overlay"
        >
          <button className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors" onClick={() => setLightboxImage(null)}>
            <X size={28} />
          </button>
          <motion.img 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            src={lightboxImage} 
            alt="What's On" 
            className="max-w-full max-h-[90vh] object-contain rounded-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}
    </div>
  );
}
